import React, { useMemo } from 'react';
import { MUSIC_VIDEO_EFFECTS } from 'fym-shared/src/music24/musicVideoEffects';
import { effectsAt } from 'fym-shared/src/music24/musicVideoEffectFrames';
import { parseReleaseThemeId, themeColours } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../common/types';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { LyricSketch } from './effects/LyricSketch';
import { ShotSketch } from './effects/ShotSketch';
import { TitleCardSketch } from './effects/TitleCardSketch';
import { TransitionSketch } from './effects/TransitionSketch';
import { previewLyricLines } from './previewEffectsMath';

interface PreviewEffectsProps {
  /** The second of the song on screen. */
  sec: number;
  /** `picture`: the transitions and the shot effects (under the texts). `text`: the cards and the lyric moments. */
  layer: 'picture' | 'text';
}

/**
 * The effects of the cut in the live preview (spec 2026-10-17 directed video §8): a CSS sketch of
 * each family at the right time, with the shared time maths (`musicVideoEffectFrames.ts`). The move
 * of the footage (whip, zoom, shake, colour split) is on the footage itself (`PreviewFrame`); this
 * draws what goes over it. The video file shows the full look of the Remotion components.
 */
const PreviewEffectsComponent: React.FC<PreviewEffectsProps> = ({ sec, layer }) => {
  const { doc, data, songMap } = useMusicVideoEditor();
  const accent = useMemo(
    () => themeColours(parseReleaseThemeId(data.song.releaseThemeId), data.song.palette ?? []).accent,
    [data.song.palette, data.song.releaseThemeId]
  );
  const lines = useMemo(() => previewLyricLines(data.lyrics.lines), [data.lyrics.lines]);
  const active = useMemo(() => effectsAt(doc.effects, sec), [doc.effects, sec]);
  if (active.length === 0) {
    return null;
  }
  return (
    <>
      {active.map((effect) => {
        const { family } = MUSIC_VIDEO_EFFECTS[effect.kind];
        if (layer === 'picture') {
          if (family === 'transition') {
            return <TransitionSketch key={effect.id} effect={effect} sec={sec} accent={accent} />;
          }
          if (family === 'shot') {
            return <ShotSketch key={effect.id} effect={effect} sec={sec} accent={accent} beats={songMap.beats} />;
          }
          return null;
        }
        if (family === 'title') {
          return (
            <TitleCardSketch
              key={effect.id}
              effect={effect}
              sec={sec}
              accent={accent}
              songTitle={data.song.title}
              artistName={data.song.artistName}
            />
          );
        }
        if (family === 'lyric') {
          return (
            <LyricSketch
              key={effect.id}
              effect={effect}
              sec={sec}
              accent={accent}
              lines={lines}
              beats={songMap.beats}
            />
          );
        }
        return null;
      })}
    </>
  );
};

export const PreviewEffects = TypedMemo(PreviewEffectsComponent);
